import { Avatar, Checkbox, Flex, Form, Segmented, Typography } from "antd"
import { useTranslation } from "react-i18next"

import { useIncognito } from "../../hooks/useIncognito"
import Icon from "../../Icon"
import ButtonComponent from "../Button"
import InputComponent from "../Input"
import { INCOGNITO_MASK } from "../Private"
import SelectComponent from "../Select"

const { Text } = Typography

const defaultValues = {
  role_id: 2
}

const AvatarBA = ({ businessAccount }) => {
  return (
    <Avatar
      className="user_avatar"
      size={16}
      style={{
        backgroundColor: businessAccount?.photo ? "transparent" : "#225AEA",
        borderRadius: 4,
        border: "none",
        fontSize: 12,
        marginRight: 10,
        minWidth: 16
      }}
      src={businessAccount?.photo || null}
    >
      {!businessAccount?.photo ? businessAccount?.name?.[0] : null}
    </Avatar>
  )
}

function InviteForm({
  onClose,
  onSubmit,
  initialValues,
  businessAccountList,
  roles,
  isMobile,
  isBusinessSettingsPage,
  businessAccount
}) {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const business_accounts_ids = Form.useWatch("business_accounts_ids", form)
  const incognito = useIncognito()
  // Editing an existing member shows their real name/email/BA access - mask that in Incognito
  // mode. A fresh invite has nothing sensitive to mask yet (the admin is typing it in).
  const shouldMask = !!initialValues && incognito

  const onFinish = (values) => {
    onSubmit(values)
  }

  function handleClearError(e, field) {
    form.setFields([
      {
        name: field,
        errors: []
      }
    ])
  }

  function onFocusHandler(e, fieldName) {
    const field = fieldName || e.target.name || e.target.id
    handleClearError(e, field)
  }

  const addErrorIcon = (error) => (
    <span>
      <Icon type={"alert_triangle_error"} /> {error}
    </span>
  )

  const optionsTypeFinal = roles.map((role) => ({
    label: role?.name,
    value: role?.id,
    disabled: role?.name === "Owner"
  }))
  const validateMessages = {
    required: addErrorIcon("Required fields were not filled.")
  }

  const businessAccountOptions = (
    isBusinessSettingsPage ? [businessAccount] : businessAccountList
  )?.map(({ name, id, photo }) => ({
    label: name,
    value: id,
    name,
    photo
  }))

  return (
    <Form
      className={"with-default-error"}
      onFinish={onFinish}
      validateTrigger="onSubmit"
      validateMessages={validateMessages}
      layout="vertical"
      clearOnDestroy={true}
      form={form}
      {...(initialValues
        ? {
            initialValues: {
              ...initialValues,
              role_id: initialValues?.role?.id,
              business_accounts_ids: initialValues?.["access_granted"]?.id
            }
          }
        : isBusinessSettingsPage
          ? { initialValues: { ...defaultValues, business_accounts_ids: businessAccount?.id } }
          : { initialValues: defaultValues })}
    >
      <div>
        <Form.Item name="name" label={t("Name")} rules={[{ required: true }]} hidden={shouldMask}>
          <InputComponent
            placeholder={t("Enter user name")}
            name="name"
            disabled={!!initialValues}
            onFocus={onFocusHandler}
          />
        </Form.Item>
        {shouldMask && (
          <Form.Item label={t("Name")}>
            <InputComponent disabled value={INCOGNITO_MASK} />
          </Form.Item>
        )}

        <Form.Item
          label={t("Email address")}
          name="email"
          rules={[
            { required: true, message: t("Please insert user's Email") },
            { type: "email", message: t("The email you entered is incorrect") }
          ]}
          hidden={shouldMask}
        >
          <InputComponent
            placeholder={t("e.g. email@gmail.com")}
            name="email"
            disabled={!!initialValues}
            type="email"
          />
        </Form.Item>
        {shouldMask && (
          <Form.Item label={t("Email address")}>
            <InputComponent disabled value={INCOGNITO_MASK} />
          </Form.Item>
        )}

        <Form.Item
          name="role_id"
          label={t("User permission")}
          rules={[{ required: true }]}
          tooltip={{
            title: (
              <Flex vertical={true} gap={20}>
                <Flex vertical={true} gap={4}>
                  <Text style={{ color: "#FFFFFF", fontSize: 12 }} strong>
                    {t("Administrator")}
                  </Text>
                  <Text style={{ fontSize: 12 }} type="secondary">
                    {t(
                      "This role is for people who need the same access as the account owner. This role can see and manage everything."
                    )}
                  </Text>
                </Flex>

                <Flex vertical={true} gap={4}>
                  <Text style={{ color: "#FFFFFF", fontSize: 12 }} strong>
                    {t("Manager")}
                  </Text>
                  <Text style={{ fontSize: 12 }} type="secondary">
                    {t(
                      "This role is for people who need edit access, but can't manage integrations or manage billing."
                    )}
                  </Text>
                </Flex>

                <Flex vertical={true} gap={4}>
                  <Text style={{ color: "#FFFFFF", fontSize: 12 }} strong>
                    {t("View only")}
                  </Text>
                  <Text style={{ fontSize: 12 }} type="secondary">
                    {t(
                      "This role is for people who need view access, but can't edit or change anything. This role can also export data and download reports."
                    )}
                  </Text>
                </Flex>
              </Flex>
            ),
            icon: (
              <div>
                <Icon type={"circle_info"} />
              </div>
            ),
            overlayStyle: { minWidth: 234 }
          }}
        >
          <Segmented
            options={optionsTypeFinal}
            name="type"
            size="large"
            block
            style={{ fontWeight: 500 }}
          />
        </Form.Item>

        <Form.Item
          label={t("Grant access")}
          name="business_accounts_ids"
          rules={[{ required: true }]}
          hidden={shouldMask}
        >
          <SelectComponent
            size="large"
            mode={initialValues || isBusinessSettingsPage ? null : "multiple"}
            name="business_accounts_ids"
            disabled={!!initialValues || isBusinessSettingsPage}
            onFocus={onFocusHandler}
            placeholder={t("Select business accounts with access")}
            options={businessAccountOptions}
            menuItemSelectedIcon={null}

            optionRender={(option) => {
              return (
                <Flex gap={8} align="center" style={{ padding: 1 }}>
                  <Checkbox checked={business_accounts_ids?.includes(option?.value)} />
                  <AvatarBA businessAccount={option?.data} />
                  {option?.label}
                </Flex>
              )
            }}
            maxTagCount={1}
            //maxTagPlaceholder={(omittedValues) => `+ ${omittedValues?.length}`}
            labelRender={({ label }) => label}
          />
        </Form.Item>
        {shouldMask && (
          <Form.Item label={t("Grant access")}>
            <InputComponent disabled value={INCOGNITO_MASK} />
          </Form.Item>
        )}
      </div>
      <Flex gap={12} justify="end" vertical={isMobile} style={{ padding: "24px 3px 3px 3px" }}>
        <ButtonComponent
          size="large"
          htmlType="button"
          onClick={onClose}
          style={isMobile ? { width: "100%" } : {}}
        >
          {t("Cancel")}
        </ButtonComponent>

        <ButtonComponent
          size="large"
          type="primary"
          htmlType="submit"
          style={isMobile ? { width: "100%" } : {}}
        >
          {t(initialValues ? "Confirm" : "Send invite")}
        </ButtonComponent>
      </Flex>
    </Form>
  )
}

export default InviteForm
